import { Box, Flex, SimpleGrid, Spinner } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  fetchLeaderboardThunk,
  useLeaderboardSelector,
} from "store/slices/leaderboard";
import { leaderboardColumns } from "./leaderboardColumns";
import { DATA_FETCHING_STATUSES } from "lib/constants";
import { LeaderboardTable } from "./leaderboardTable";
import { formatDate } from "lib/helpers";
import { formatGameTime } from "lib/helpers";
import { formatPayloadDate } from "lib/helpers";
// import { useColors } from "components/customs/colors";

export function Leaderboard() {
  // const { textColor } = useColors();
  const dispatch = useDispatch();
  const [activeButton, setActiveButton] = useState("score");
  const leaderboardState = useLeaderboardSelector();
  const leaderboard = leaderboardState?.leaderboard;
  const totalPages = leaderboard?.data?.totalPages;

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFilter, setSelectedFilter] = useState("daily");
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    if (activeButton === "score") {
      setSelectedFilter("daily");
      setSelectedDate(new Date());
    } else {
      setSelectedFilter("allTime");
    }
    setCurrentPage(1);
  }, [activeButton]);

  useEffect(() => {
    const params = {
      page: currentPage,
      type: activeButton,
      filter: selectedFilter,
    };
    
    // Add date parameter when filter is daily
    if (selectedFilter === "daily") {
      params.date = selectedDate;
    }
    
    dispatch(fetchLeaderboardThunk(params));
  }, [currentPage, dispatch, activeButton, selectedFilter, selectedDate]);

  const conditionalColumns = useMemo(() => {
    // Base columns: Rank, Name, Player ID, Game Time
    const baseColumns = [
      { Header: "Rank", accessor: "rank" },
      { Header: "Name", accessor: "name" },
      { Header: "Player ID", accessor: "player_id" }, // value is user id
      { Header: "Game Time", accessor: "gameTime" },
    ];

    // Add the metric column based on activeButton
    if (activeButton === "mostHits") {
      return [
        ...baseColumns.slice(0, 3),
        { Header: "Hits", accessor: "total_hits" },
        baseColumns[3],
      ];
    }

    if (activeButton === "gamesPlayed") {
      return [
        ...baseColumns.slice(0, 3),
        { Header: "Games Played", accessor: "games_played" },
        baseColumns[3],
      ];
    }

    // Default: score
    return [
      ...baseColumns.slice(0, 3),
      { Header: "Score", accessor: "score" },
      baseColumns[3],
    ];
  }, [activeButton]);

  const tableRows = useMemo(() => {
    const results = leaderboard?.data?.results ?? [];
    const selectedDateKey =
      selectedFilter === "daily" && selectedDate
        ? formatPayloadDate(selectedDate)
        : null;
    const filteredResults = selectedDateKey
      ? results.filter(
          (boardItem) =>
            formatPayloadDate(boardItem?.gameDate) === selectedDateKey
        )
      : results;
    return (
      filteredResults
        // .slice()
        // .sort((a, b) => {
        //   if (a?.score === b?.score && a?.gameTime < b?.gameTime) return -1;
        //   if (a?.score === b?.score && a?.gameTime > b?.gameTime) return 1;
        //   if (a?.score > b?.score) return -1;
        //   if (a?.score < b?.score) return 1;
        //   return 0;
        // })
        .map((boardItem, index) => ({
          id: boardItem.id,
          rank: index + 1,
          gameTime: formatGameTime(boardItem.gameTime),
          name: boardItem?.userId?.name ?? boardItem?.userId?.username ?? "",
          player_id: boardItem?.userId?._id ?? "N/A",
          ...(activeButton === "mostHits"
            ? { total_hits: boardItem?.totalHits ?? "N/A" }
            : {}),
          ...(activeButton === "gamesPlayed"
            ? { games_played: boardItem?.totalGames ?? "N/A" }
            : {}),
          score: boardItem?.score ?? "N/A",

          joined_date: boardItem?.gameDate
            ? formatDate(new Date(boardItem?.gameDate))
            : "N/A",
        }))
    );
  }, [leaderboard, activeButton]);

  let component = (
    <Flex justifyContent="center" mt="28">
      <Spinner size="xl" color="brand.s" />
    </Flex>
  );

  if (leaderboard?.status === DATA_FETCHING_STATUSES.DATA_LOADED) {
    component = (
      <LeaderboardTable
        columnsData={conditionalColumns}
        tableData={tableRows}
        totalPages={totalPages}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        activeButton={activeButton}
        setActiveButton={setActiveButton}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
      />
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <SimpleGrid mb="20px" spacing={{ base: "20px", xl: "20px" }}>
        {component}
      </SimpleGrid>
    </Box>
  );
}
